import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect
} from 'react-router-dom';
import Loadable from 'react-loadable';
import PageLoader from '@components/common/PageLoader';
import OnRouteChange from '@containers/common/OnRouteChange';
import { APP_CONFIG } from '@constants';
import Notification from '@components/common/notification';
import { flowType } from '@utility/userInfo';
import { getObjectValue } from '@utility/utills';
import ScreenSaver from '@containers/screenSaver';
import ErrorPopup from './components/common/ErrorPopUp/index';
import LoadingSuccess from './components/common/LoadingSuccess/index';

const PrivateRoute = ({ component: Component, authed, ...rest }) => {
  const userInfo = getObjectValue(() => JSON.parse(window.sessionStorage.getItem('userInfo'), false));
  return (
    <Route
      {...rest}
      render={(props) => { return userInfo ? <Component {...props} /> : <Redirect to={{ pathname: '/checkIn', state: { from: props.location } }} />; }}
    />
  );
};

const PrivateRouteForActivation = ({ component: Component, authed, ...rest }) => {
  const deviceInfo = getObjectValue(() => JSON.parse(window.sessionStorage.getItem('deviceInfo'), false));
  const storeInfo = getObjectValue(() => JSON.parse(window.sessionStorage.getItem('auth'), false));
  return (
    <Route
      {...rest}
      render={(props) => { return deviceInfo && storeInfo ? <Component {...props} /> : <Redirect to={{ pathname: '/activate', state: { from: props.location } }} />; }}
    />
  );
};

const PublicRoute = ({ component: Component, ...rest }) => {
  const deviceInfo = getObjectValue(() => JSON.parse(window.sessionStorage.getItem('deviceInfo'), false));
  const storeInfo = getObjectValue(() => JSON.parse(window.sessionStorage.getItem('auth'), false));
  return (
    <Route
      {...rest}
      render={(props) => { return deviceInfo && storeInfo ? <Redirect to={{ pathname: `${flowType()}/checks`, state: { from: props.location } }} /> : <Component {...props} />; }}
    />
  );
};

const LoadingComponent = ({ isLoading, isError }) => {
  if (isLoading) {
    return <PageLoader />;
  }
  if (isError) {
    return (
      <div>
        Sorry, unable to load the page
      </div>
    );
  }
  return null;
};

const Orders = Loadable({
  loader: () => import('@containers/MenuOrder'),
  loading: LoadingComponent
});

const UpSell = Loadable({
  loader: () => import('@containers/KioskUpsell'),
  loading: LoadingComponent
});

const notFound = Loadable({
  loader: () => import('@components/404NotFound'),
  loading: LoadingComponent,
});

const StoreChecks = Loadable({
  loader: () => import('@containers/StoreChecks'),
  loading: LoadingComponent,
});

const Activation = Loadable({
  loader: () => import('@containers/Activation'),
  loading: LoadingComponent,
});

const OrderItemDetail = Loadable({
  loader: () => import('@containers/OrderItemDetail'),
  loading: LoadingComponent
});

const Receipt = Loadable({
  loader: () => import('@containers/ViewReceipt'),
  loading: LoadingComponent
});
const GameCard = Loadable({
  loader: () => import('@containers/GameCard'),
  loading: LoadingComponent
});

const GiftCard = Loadable({
  loader: () => import('@containers/GiftCard'),
  loading: LoadingComponent
});

const CallCenterError = Loadable({
  loader: () => import('@containers/CallCenterError'),
  loading: LoadingComponent
});

const Stage = Loadable({
  loader: () => import('@containers/Stage'),
  loading: LoadingComponent,
});

const StagePayment = Loadable({
  loader: () => import('@containers/StagePayment'),
  loading: LoadingComponent,
});

const VirtualKeyboard = Loadable({
  loader: () => import('@formElements/SimpleKeyboard/virtualKeyboard'),
  loading: LoadingComponent,
});

const CheckIn = Loadable({
  loader: () => import('@containers/CheckIn'),
  loading: LoadingComponent,
});

const FireboardChecks = Loadable({
  loader: () => import('@containers/FireboardChecks'),
  loading: LoadingComponent,
});

const TableTop = Loadable({
  loader: () => import('@containers/TableTop'),
  loading: LoadingComponent,
});

const Kiosk = Loadable({
  loader: () => import('@containers/Kiosk'),
  loading: LoadingComponent,
});

const Callcenter = Loadable({
  loader: () => import('@containers/CallCenter'),
  loading: LoadingComponent,
});

const PaymentTerminal = Loadable({
  loader: () => import('@containers/PaymentTerminal'),
  loading: LoadingComponent,
});

const Cubby = Loadable({
  loader: () => import('@containers/Cubby'),
  loading: LoadingComponent,
});

const OrderUp = Loadable({
  loader: () => import('@containers/OrderUp'),
  loading: LoadingComponent,
});

const KitchenDisplaySystem = Loadable({
  loader: () => import('@containers/KitchenDisplaySystem'),
  loading: LoadingComponent,
});

const QrScanner = Loadable({
  loader: () => import('@containers/QrScanner'),
  loading: LoadingComponent,
});

const CashOut = Loadable({
  loader: () => import('@containers/CounterCash'),
  loading: LoadingComponent,
});

const ErrorPage = Loadable({
  loader: () => import('@components/common/ErrorPage'),
  loading: LoadingComponent,
});

const CashIn = Loadable({
  loader: () => import('@containers/CounterCash/counterCash'),
  loading: LoadingComponent,
});

const ServerCashOutReport = Loadable({
  loader: () => import('@components/serverCashoutReport'),
  loading: LoadingComponent,
});

// const CustomKeyboard = Loadable({
//   loader: () => import('@components/common/CustomKeyboard/textkeyboard'),
//   loading: LoadingComponent,
// });
const LoyaltyBalance = Loadable({
  loader: () => import('@containers/ViewReceipt/loyaltyBalance'),
  loading: LoadingComponent,
});

export default (
  <Router>
    <div className="od-app-background-color background-primary page od-font-family">
      <Notification />
      <ScreenSaver />
      <OnRouteChange>
        <Switch>
          <PublicRoute exact path={`${APP_CONFIG.BASE_URL}/`} component={Activation} />
          <PublicRoute exact path={`${APP_CONFIG.BASE_URL}/callcenter/landing/tsdglobal`} component={Activation} />
          {/* <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/serverApp/keyboard`} component={CustomKeyboard} /> */}
          <PublicRoute exact path={`${APP_CONFIG.BASE_URL}/activate`} component={Activation} />
          <PublicRoute exact path={`${APP_CONFIG.BASE_URL}/activationCode/:deviceExternalId`} component={Activation} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/orders`} component={Orders} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/upsell`} component={UpSell} />
          {/* <PrivateRouteForActivation exact path={`${APP_CONFIG.BASE_URL}/selected-cart/:menuItemId`} component={MultipleSelectedCart} /> */}
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/serverApp/checks`} component={StoreChecks} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/fireboard/checks`} component={FireboardChecks} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/tableTop/checks`} component={TableTop} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/stage/:checkId`} component={Stage} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/order-item/:menuTypeId/:menuItemId/:internalId/:type`} component={OrderItemDetail} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/order-item/:menuTypeId/:menuItemId`} component={OrderItemDetail} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/order-child/:menuTypeId/:menuItemId`} component={OrderItemDetail} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/stagepayment/:checkId`} component={StagePayment} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/receipt/:checkId`} component={Receipt} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/gamecard/:checkId`} component={GameCard} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/giftcard/:checkId`} component={GiftCard} />
          <PrivateRoute exact path={`${APP_CONFIG.BASE_URL}/callCenterError`} component={CallCenterError} />
          <PrivateRouteForActivation exact path={`${APP_CONFIG.BASE_URL}/checkIn`} component={CheckIn} />
          <PrivateRouteForActivation exact path={`${APP_CONFIG.BASE_URL}/cubby/checks`} component={Cubby} />
          <PrivateRouteForActivation exact path={`${APP_CONFIG.BASE_URL}/orderUp/checks`} component={OrderUp} />
          <PrivateRouteForActivation exact path={`${APP_CONFIG.BASE_URL}/kds/checks`} component={KitchenDisplaySystem} />
          <Route exact path={`${APP_CONFIG.BASE_URL}/qrScan`} component={QrScanner} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/kiosk`} component={Kiosk} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/callcenter`} component={Callcenter} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/cashOut`} component={CashOut} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/paymentTerminal`} component={PaymentTerminal} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/cashIn`} component={CashIn} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/report/server/cashOut`} component={ServerCashOutReport} />
          <PrivateRoute path={`${APP_CONFIG.BASE_URL}/loyalty-balance`} component={LoyaltyBalance} />
          <Route exact path={`${APP_CONFIG.BASE_URL}/error`} component={ErrorPage} />
          <Route path="*" component={notFound} />
        </Switch>
        <VirtualKeyboard />
      </OnRouteChange>
      <ErrorPopup/>
      <LoadingSuccess/>
    </div>
  </Router>
);
